import { ComponentPropsWithRef, forwardRef, ReactNode } from "react";
import classNames from "classnames";
import { Merge } from "@/@types/utils.ts";

type TProps = {
  label?: ReactNode;
  endlabel?: ReactNode;
  id: string;
  value: string;
  containerClassName?: string;
  inputClassName?: string;
  checked: boolean;
  onChange?: (e: TRadioProps["value"]) => void;
  size?: "sm" | "md" | "lg";
  color?: string;
};

export type TRadioProps = Merge<ComponentPropsWithRef<"input">, TProps>;

const Radio = forwardRef<HTMLInputElement, TRadioProps>((props, ref) => {
  const {
    label,
    value,
    className = "",
    onChange,
    endlabel,
    checked = false,
    id,
    disabled = false,
    size = "md",
    color = "gray-800",
    containerClassName,
    inputClassName,
    ...restProps
  } = props;

  return (
    <div
      className={classNames(
        "shadow-none flex items-center gap-2 justify-between cursor-pointer",
        {
          "!cursor-default opacity-30": disabled,
        },
        containerClassName,
      )}
      onClick={() => {
        if (!disabled) {
          onChange && onChange(value);
        }
      }}
    >
      {!!label && (
        <div className="text-[14px] leading-5 text-gray-700">{label}</div>
      )}

      <div className={classNames("flex items-center gap-2", className)}>
        <div
          className={classNames(
            "bg-white dark:bg-gray-100 rounded-full flex flex-shrink-0 justify-center items-center relative",
            {
              "w-3 h-3": size === "sm",
              "w-4 h-4": size === "md",
              "w-5 h-5": size === "lg",
            },
          )}
        >
          <input
            {...restProps}
            ref={ref}
            type="radio"
            disabled={disabled}
            value={value}
            id={id}
            onChange={() => {
              if (!disabled) {
                onChange && onChange(value);
              }
            }}
            checked={checked}
            className={classNames(
              "appearance-none border rounded-full border-gray-400 absolute w-full h-full checked:border-none",
              {
                "cursor-pointer": !disabled,
                "!border-none": checked,
              },
              inputClassName,
            )}
          />
          <div
            className={classNames(
              `invisible border-[6px] rounded-full w-full h-full z-1 border-${color}`,
              {
                "!visible": checked,
                "!border-[4px]": size === "sm",
                "!border-[5px]": size === "md",
                "!border-[6px]": size === "lg",
              },
            )}
          />
        </div>
      </div>
      {!!endlabel && (
        <div className={"text-[14px] leading-5 text-gray-700"}>{endlabel}</div>
      )}
    </div>
  );
});

export default Radio;
